import type {
  ScannedFoot,
  ScannedLine,
  SylClass,
} from "@/lib/tool-kit/poetry/meter";

/** The marks a scansion is drawn with. */
export const MARK = {
  stressed: "/",
  unstressed: "˘",
  flexible: "×",
  unknown: "?",
} as const;

export interface SyllableMark {
  /** The character drawn under the word. */
  mark: string;
  cls: SylClass;
  /**
   * `hard` — a stressed syllable in a weak slot, an unstressed one in a strong slot, or one
   * that fits no slot. `soft` — a stressed one-syllable word in a weak slot, which verse
   * often demotes. `none` — the syllable sits where the pattern puts it.
   */
  problem: "none" | "soft" | "hard";
}

export interface WordMarks {
  text: string;
  key: string;
  start: number;
  marks: SyllableMark[];
}

function markFor(cls: SylClass): string {
  switch (cls) {
    case "S":
    case "M":
    case "x":
      return MARK.stressed;
    case "w":
      return MARK.unstressed;
    case "f":
      return MARK.flexible;
    default:
      return MARK.unknown;
  }
}

function severity(cls: SylClass, strong: boolean): SyllableMark["problem"] {
  if ((cls === "S" && !strong) || (cls === "w" && strong)) {
    return "hard";
  }
  return cls === "M" && !strong ? "soft" : "none";
}

/** One entry per word, with a mark for each of its syllables. */
export function wordMarks(scanned: ScannedLine): WordMarks[] {
  const { alignment, template } = scanned.fit;
  const slotOf = new Map<number, boolean>();
  for (const [slot, at] of alignment.slotSyllable.entries()) {
    if (at !== null) {
      slotOf.set(at, template.slots[slot]);
    }
  }
  const extras = new Set(alignment.extras);
  const words: WordMarks[] = scanned.line.words.map((word) => ({
    text: word.text,
    key: word.key,
    start: word.start,
    marks: [],
  }));
  for (const [index, syl] of scanned.syls.entries()) {
    const strong = slotOf.get(index);
    let problem: SyllableMark["problem"] = "none";
    if (extras.has(index)) {
      problem = "hard";
    } else if (strong !== undefined) {
      problem = severity(syl.cls, strong);
    }
    words[syl.wordIndex].marks.push({
      mark: markFor(syl.cls),
      cls: syl.cls,
      problem,
    });
  }
  return words.filter((word) => word.marks.length > 0);
}

/** "˘ / | ˘ / | / ˘" — a line's feet as marks. */
export function feetText(feet: readonly ScannedFoot[]): string {
  return feet
    .map((foot) =>
      [...foot.pattern]
        .map((c) => (c === "S" ? MARK.stressed : MARK.unstressed))
        .join(" ")
    )
    .join(" | ");
}

/** A plain-text scansion for copying: each line, then its marks. */
export function scansionText(lines: readonly ScannedLine[]): string {
  return lines
    .map((scanned) => {
      const marks = wordMarks(scanned)
        .map((word) => word.marks.map((m) => m.mark).join(""))
        .join(" ");
      return `${scanned.line.text.trim()}\n${marks}\n${feetText(scanned.feet)}`;
    })
    .join("\n\n");
}
